import { type PartState, PartStates } from "./part-state.js";
import { SentencePicker, skipKey, skipped } from "./sentence-picker.js";
import {
  sentenceChoice,
  type SentencePart,
  SentenceSheet,
} from "./sentence-sheet.js";
import { SheetSentences } from "./sheet-sentences.js";
import {
  concurrency,
  counted,
  pageFetch,
  type SheetKind,
  sheetTitle,
  type SheetToolProperties,
  startSheetTool,
} from "./sheet-tool.js";
import { GlyphLibrary } from "./worksheet-drawing.js";
import { sheetOrder } from "./worksheet-seed.js";

/**
 * The sentence worksheet generator: the 句子 sheet as a kind of sheet
 * `sheet-tool.ts` draws, from 例句 rather than words.
 *
 * A sheet's sentences are a List's, one of the reader's own Lists' or the
 * reader's saved ones, fetched one at a time. Every sentence is shown with a
 * box, so a teacher can leave some out, and a sheet can leave out the long
 * ones. Typing words in is no source here, since a word alone names no
 * sentence to fetch.
 */

/** The parts, in the order they print, and whether each prints by default. */
export const sentenceParts = new PartStates<SentencePart>([
  ["gaps", true],
  ["arrange", true],
  ["pinyin", true],
  ["translate", false],
  ["key", true],
]);

/** The lengths a sheet can keep its sentences to, in 汉字. */
export const sentenceLengths = [0, 8, 12, 16] as const;

/** A sentence sheet's settings. */
export type SentenceState = PartState<SentencePart> & {
  /** The sentences left out, by `skipKey`, joined by full stops. */
  readonly skip: string;

  /** The most 汉字 a sentence may have, or 0 for any. */
  readonly longest: number;
};

/** Reads and writes a sentence sheet's settings. */
export const sentenceStates = {
  read(hash: string): SentenceState {
    const parameters = new URLSearchParams(hash.replace(/^#/u, ""));
    const parts = sentenceParts.read(hash);
    const longest = Number(parameters.get("longest") ?? "0");

    return {
      ...parts,
      // A sentence sheet has no box to type words into, and a List is what
      // most sheets are drawn from.
      source: parts.source === "typed" ? "list" : parts.source,
      skip: (parameters.get("skip") ?? "").replaceAll(/[^0-9a-f.]/gu, ""),
      longest: (sentenceLengths as readonly number[]).includes(longest)
        ? longest
        : 0,
    };
  },

  fragment(state: SentenceState): string {
    const parameters = new URLSearchParams(sentenceParts.fragment(state));
    parameters.delete("text");
    if (state.skip !== "") {
      parameters.set("skip", state.skip);
    }
    if (state.longest !== 0) {
      parameters.set("longest", String(state.longest));
    }

    return parameters.toString();
  },
};

const han = /\p{Script=Han}/u;

/** How many 汉字 a sentence has. */
function length(text: string): number {
  return Array.from(text).filter((letter) => han.test(letter)).length;
}

/** What drawing a sentence sheet needs from its page. */
export interface SentenceSheetKindProperties {
  readonly sentences: SheetSentences;

  /** The boxes to leave sentences out, where the page has them. */
  readonly picker: SentencePicker | undefined;
}

/** The sentence sheet, drawn from the 例句 its source names. */
export function sentenceSheetKind(
  properties: SentenceSheetKindProperties,
): SheetKind<SentenceState> {
  return {
    material: "sentences",
    read: (hash) => sentenceStates.read(hash),
    fragment: (state) => sentenceStates.fragment(state),
    nothingAsked: (state) =>
      state.gaps || state.arrange || state.pinyin || state.translate
        ? undefined
        : "Tick at least one exercise under Include.",
    title: (state, list) =>
      sheetTitle(state, list, {
        typed: "Sentence practice",
        saved: "My saved sentences",
      }),
    changesMore: (state) => state.longest !== 0,

    draw: async ({ state, words, sentences, labels }) => {
      const found = await properties.sentences.some(sentences);
      if (found.length === 0) {
        properties.picker?.hide();

        return "Could not load the sentences. Try again in a moment.";
      }
      const fitting =
        state.longest === 0
          ? found
          : found.filter((sentence) => length(sentence.text) <= state.longest);
      const left = skipped(state.skip);
      properties.picker?.show(fitting, left);
      if (fitting.length === 0) {
        return `All these sentences are longer than ${String(state.longest)} characters. Allow longer sentences under More settings.`;
      }
      const chosen = fitting.filter(
        (sentence) => !left.has(skipKey(sentence.uuid)),
      );
      if (chosen.length === 0) {
        return "Tick at least one sentence to print.";
      }
      const choice = sentenceChoice(chosen, words, state);
      const asked =
        choice.gaps.length +
        choice.arrange.length +
        choice.pinyin.length +
        choice.translate.length;
      if (asked === 0) {
        return "The chosen sentences cannot be used for these exercises. Tick another exercise or choose more sentences.";
      }

      const library = new GlyphLibrary({ document });
      const pages = new SentenceSheet({ library }).pages(
        state,
        {
          gaps: sheetOrder(choice.gaps, state),
          arrange: sheetOrder(choice.arrange, state),
          pinyin: sheetOrder(choice.pinyin, state),
          translate: sheetOrder(choice.translate, state),
        },
        labels,
      );

      return {
        nodes: [library.element, ...pages],
        status: `${counted(asked, "sentence", "sentences")} on ${counted(pages.length, "page", "pages")}.`,
      };
    },
  };
}

/** Start the sentence worksheet generator on its container. */
export function startSentenceWorksheetTool(
  container: HTMLElement,
  properties: SheetToolProperties = pageFetch,
): void {
  startSheetTool(
    container,
    sentenceSheetKind({
      sentences: new SheetSentences({ fetch: properties.fetch, concurrency }),
      picker: SentencePicker.on(container),
    }),
    properties,
  );
}
